import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ROUTE_LEG_FIELDS } from "@/lib/routeSync";
import { fmt, fmtSpeed, minToHm } from "@/lib/units";
import { Wind, Navigation, Pencil, Link2, FileSpreadsheet, Crop } from "lucide-react";
import NavLogTable from "./NavLogTable";
import FileAttachments from "./FileAttachments";
import VacCaptureDialog from "./VacCaptureDialog";
import { useVacLibrary, selectedVacCodes } from "@/lib/vac";

const Stat = ({ label, value, sub, tid }) => (
  <div className="glass rounded-xl p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400">{label}</div>
    <div className="mt-1 font-mono-data text-2xl font-bold text-slate-100" data-testid={tid}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

export default function NavLogTab({ briefing, setBriefing, aircraft, nav, route, onEditRoute, onRouteLegChange, onExportExcel, exporting, briefingId }) {
  const first = briefing.legs?.[0] || {};
  const [gwd, setGwd] = useState(first.windDir || 0);
  const [gws, setGws] = useState(first.windSpeed || 0);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [atisKey, setAtisKey] = useState(0);
  const { charts } = useVacLibrary();

  const setLeg = (listKey) => (i, key, v) => {
    const val = v === "" ? "" : Number(v);
    setBriefing((b) => {
      const legs = [...(b[listKey] || [])];
      legs[i] = { ...legs[i], [key]: val };
      return { ...b, [listKey]: legs };
    });
    if (route && ROUTE_LEG_FIELDS.includes(key)) onRouteLegChange(i, key, val, listKey);
  };

  const applyWindAll = (dir, spd) =>
    setBriefing((b) => {
      const w = (ls) => (ls || []).map((l) => ({ ...l, windDir: dir, windSpeed: spd }));
      return { ...b, legs: w(b.legs), returnLegs: w(b.returnLegs) };
    });

  const dep = briefing.departure || "?";
  const arr = briefing.arrival || "?";
  const vacCodes = selectedVacCodes(briefing);
  const captureCharts = charts.filter((c) => vacCodes.includes(c.icao));
  const targets = [{ pointId: "atis::aller", label: nav.ret ? `ATIS ${arr} (aller)` : `ATIS ${arr}` },
    ...(nav.ret ? [{ pointId: "atis::retour", label: `ATIS ${dep} (retour)` }] : [])];

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button size="sm" onClick={onExportExcel} disabled={exporting} data-testid="navlog-export-excel-button"
          className="bg-emerald-500 font-semibold text-[#070C18] hover:bg-emerald-400">
          <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Exporter le log (Excel)
        </Button>
      </div>
      {route && (
        <div className="glass flex flex-wrap items-center justify-between gap-3 rounded-2xl px-5 py-3" data-testid="navlog-route-bar">
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Link2 className="h-4 w-4 text-cyan-400" />
            Route liée : <span className="font-semibold text-slate-100" data-testid="navlog-route-name">{route.name}</span>
            <span className="text-xs text-slate-500">· Alt, Rv et Décl modifiés ici sont enregistrés dans la route</span>
          </div>
          <Button size="sm" variant="outline" onClick={onEditRoute} data-testid="navlog-edit-route-button"
            className="border-slate-700 bg-transparent text-cyan-300 hover:bg-cyan-500/10">
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Modifier la route
          </Button>
        </div>
      )}
      <div className="glass rounded-2xl p-5">
        <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">
          <Wind className="h-4 w-4" /> Vent global (appliquer à toutes les branches{nav.ret ? ", aller et retour" : ""})
        </h3>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-32">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Direction (°)</Label>
            <Input type="number" value={gwd} data-testid="global-wind-dir"
              onChange={(e) => { const d = Number(e.target.value); setGwd(d); applyWindAll(d, gws); }}
              className="mt-1 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" />
          </div>
          <div className="w-32">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Vitesse (kt)</Label>
            <Input type="number" value={gws} data-testid="global-wind-speed"
              onChange={(e) => { const s = Number(e.target.value); setGws(s); applyWindAll(gwd, s); }}
              className="mt-1 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" />
          </div>
          <p className="text-xs text-slate-500">Vous pouvez aussi ajuster le vent branche par branche dans le tableau.</p>
        </div>
      </div>

      <NavLogTable title={nav.ret ? `Aller — ${dep} → ${arr}` : ""} nav={nav.out} setLeg={setLeg("legs")} />
      {nav.ret && <NavLogTable title={`Retour — ${arr} → ${dep}`} nav={nav.ret} setLeg={setLeg("returnLegs")} p="ret-" />}

      <div className="glass rounded-2xl p-5" data-testid="navlog-atis-images">
        <h3 className="mb-1 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">Image ATIS aérodrome d'arrivée (log Excel)</h3>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-500">Capturez une zone d'une carte VAC du briefing, ou collez (Ctrl/Cmd+V) / déposez une image : elle apparaît dans le cadre « ATIS AERODROME ARRIVEE » du log Excel (dernière image jointe).</p>
          <Button size="sm" variant="outline" onClick={() => setCaptureOpen(true)} data-testid="navlog-vac-capture-button"
            className="border-slate-700 bg-transparent text-cyan-300 hover:bg-cyan-500/10">
            <Crop className="mr-1.5 h-3.5 w-3.5" /> Capturer depuis une carte VAC
          </Button>
        </div>
        <div key={atisKey} className={`grid gap-4 ${nav.ret ? "md:grid-cols-2" : ""}`}>
          <FileAttachments briefingId={briefingId} pointId="atis::aller" title={`Arrivée ${arr}${nav.ret ? " (aller)" : ""}`} compact />
          {nav.ret && <FileAttachments briefingId={briefingId} pointId="atis::retour" title={`Arrivée ${dep} (retour)`} compact />}
        </div>
        <VacCaptureDialog open={captureOpen} onOpenChange={setCaptureOpen} charts={captureCharts} targets={targets}
          briefingId={briefingId} onSaved={() => setAtisKey((k) => k + 1)} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label={<><Navigation className="h-3.5 w-3.5 text-cyan-500" /> Distance totale</>} value={`${fmt(nav.totalDistance)} NM`} tid="navlog-total-distance" />
        <Stat label="Temps de vol" value={minToHm(nav.totalTime)} tid="navlog-total-time" />
        <Stat label={`Carburant voyage · TAS ${aircraft?.tasCruise || 0} kt`} value={`${fmt(nav.totalFuel, 1)} L`} sub={fmtSpeed(aircraft?.tasCruise)} tid="navlog-total-fuel" />
      </div>
    </div>
  );
}
